/**
 * Admin Analytics Dashboard - Phase 2
 * Real-time analytics dashboard with database-backed metrics
 */

import {
  BarChart3,
  TrendingUp,
  Users,
  MousePointerClick,
  Calendar,
  Calculator,
  Target,
  Triangle,
  ArrowLeft,
  RefreshCw,
  ExternalLink,
  Loader2,
} from "lucide-react";
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { ChatbarPanel } from "@/components/analytics/ChatbarPanel";
import { ConversionFunnel } from "@/components/analytics/ConversionFunnel";
import { CoursesPanel } from "@/components/analytics/CoursesPanel";
import { GA4Charts } from "@/components/analytics/GA4Charts";
import { SessionMetricsChart } from "@/components/analytics/SessionMetricsChart";
import { SubscriberAnalyticsPanel } from "@/components/analytics/SubscriberAnalyticsPanel";
import { UserBehaviorPanel } from "@/components/analytics/UserBehaviorPanel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useAnalyticsMetrics,
  useConversionSources,
  useToolUsageStats,
  useSessionMetrics,
  useToolInteractions,
  getTimeRange,
} from "@/hooks/useAnalytics";

interface AnalyticsMetric {
  label: string;
  value: string | number;
  change?: string;
  trend?: "up" | "down" | "neutral";
  icon: React.ReactNode;
  isLoading?: boolean;
}

export default function AnalyticsDashboard() {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState<"7d" | "30d" | "90d">("30d");

  // Calculate date range
  const dateRange = useMemo(() => getTimeRange(timeRange), [timeRange]);

  // Fetch real analytics data
  const {
    data: metricsData,
    isLoading: metricsLoading,
    refetch: refetchMetrics,
  } = useAnalyticsMetrics(dateRange);
  const {
    data: conversionsData,
    isLoading: conversionsLoading,
    refetch: refetchConversions,
  } = useConversionSources(dateRange);
  const {
    data: toolsData,
    isLoading: toolsLoading,
    refetch: refetchTools,
  } = useToolUsageStats(dateRange);
  const {
    data: sessionData,
    isLoading: sessionLoading,
    refetch: refetchSessions,
  } = useSessionMetrics(dateRange);
  const { data: toolInteractionsCount, isLoading: toolInteractionsLoading } =
    useToolInteractions(dateRange);

  // Format metrics for display
  const metrics: AnalyticsMetric[] = [
    {
      label: "Total Page Views",
      value: metricsLoading
        ? "Loading..."
        : metricsData?.totalPageViews.toLocaleString() || "0",
      icon: <BarChart3 className="h-5 w-5" />,
      isLoading: metricsLoading,
    },
    {
      label: "Unique Sessions",
      value: metricsLoading
        ? "Loading..."
        : metricsData?.uniqueSessions.toLocaleString() || "0",
      icon: <Users className="h-5 w-5" />,
      isLoading: metricsLoading,
    },
    {
      label: "Conversions",
      value: metricsLoading
        ? "Loading..."
        : metricsData?.totalConversions.toLocaleString() || "0",
      icon: <MousePointerClick className="h-5 w-5" />,
      isLoading: metricsLoading,
    },
    {
      label: "Conversion Rate",
      value: metricsLoading
        ? "Loading..."
        : `${metricsData?.conversionRate || 0}%`,
      icon: <TrendingUp className="h-5 w-5" />,
      isLoading: metricsLoading,
    },
  ];

  // Map conversion sources with icons
  const sourceIconMap: Record<string, React.ReactNode> = {
    contact_section: <Calendar className="h-4 w-4" />,
    roi_calculator: <Calculator className="h-4 w-4" />,
    feasibility_calculator: <Triangle className="h-4 w-4" />,
    readiness_assessment: <Target className="h-4 w-4" />,
  };

  const sourceLabelMap: Record<string, string> = {
    contact_section: "Contact Section",
    roi_calculator: "ROI Calculator",
    feasibility_calculator: "Feasibility Calculator",
    readiness_assessment: "Readiness Assessment",
  };

  const conversionSources =
    conversionsData?.map((item) => ({
      source: sourceLabelMap[item.source] || item.source,
      conversions: item.conversions,
      percentage: item.percentage,
      icon: sourceIconMap[item.source] || (
        <MousePointerClick className="h-4 w-4" />
      ),
    })) || [];

  // Format tool usage stats
  const toolUsageStats =
    toolsData?.map((item) => ({
      tool: item.tool_name,
      uses: item.total_uses,
      avgTime:
        item.avg_duration_seconds > 0
          ? `${Math.floor(item.avg_duration_seconds / 60)}m ${item.avg_duration_seconds % 60}s`
          : "-",
    })) || [];

  // Check if any data is loading
  const isRefreshing =
    metricsLoading || conversionsLoading || toolsLoading || sessionLoading;

  const handleRefresh = async () => {
    await Promise.all([
      refetchMetrics(),
      refetchConversions(),
      refetchTools(),
      refetchSessions(),
    ]);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="w-full px-6 py-12">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-600 hover:text-[#B87333] mb-6 transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="font-semibold">Back to Dashboard</span>
          </button>

          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-4xl font-black text-slate-900">
                  Analytics Dashboard
                </h1>
                <Badge
                  variant="outline"
                  className="bg-purple-100 text-purple-700 border-purple-300"
                >
                  Admin
                </Badge>
              </div>
              <p className="text-lg text-slate-600">
                Track website performance, conversions, and user engagement
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="flex items-center gap-2"
              >
                <RefreshCw
                  className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
                />
                Refresh
              </Button>
              <a
                href="https://analytics.google.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
              >
                <Button variant="outline" className="flex items-center gap-2">
                  <ExternalLink className="h-4 w-4" />
                  Open GA4
                </Button>
              </a>
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BarChart3 className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900 mb-1">
                Analytics Tracking Active
              </h3>
              <p className="text-sm text-blue-800">
                Google Analytics 4 and Contentsquare tracking have been
                successfully configured and are now collecting data. Real
                metrics will appear in this dashboard as users interact with
                your site. For immediate insights, use the "Open GA4" button to
                view live data in Google Analytics.
              </p>
            </div>
          </div>
        </div>

        {/* Time Range Selector */}
        <div className="mb-6 flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-700">
            Time Range:
          </span>
          <div className="flex gap-2">
            {(["7d", "30d", "90d"] as const).map((range) => (
              <Button
                key={range}
                variant={timeRange === range ? "default" : "outline"}
                size="sm"
                onClick={() => setTimeRange(range)}
                className={
                  timeRange === range ? "bg-[#B87333] hover:bg-[#A67329]" : ""
                }
              >
                {range === "7d" && "Last 7 Days"}
                {range === "30d" && "Last 30 Days"}
                {range === "90d" && "Last 90 Days"}
              </Button>
            ))}
          </div>
        </div>

        {/* Quick Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {metrics.map((metric, index) => (
            <Card
              key={index}
              className="border-2 hover:border-[#B87333] transition-colors"
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">
                  {metric.label}
                </CardTitle>
                <div className="p-2 bg-slate-100 rounded-lg text-slate-600">
                  {metric.icon}
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-900 mb-2">
                  {metric.value}
                </div>
                {metric.change && (
                  <div
                    className={`flex items-center gap-1 text-sm font-semibold ${
                      metric.trend === "up"
                        ? "text-green-600"
                        : metric.trend === "down"
                          ? "text-red-600"
                          : "text-slate-600"
                    }`}
                  >
                    {metric.trend === "up" && "↑"}
                    {metric.trend === "down" && "↓"}
                    {metric.change}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs for Different Views */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-7 max-w-[1100px]">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="conversions">Conversions</TabsTrigger>
            <TabsTrigger value="tools">Tool Usage</TabsTrigger>
            <TabsTrigger value="trends">Trends</TabsTrigger>
            <TabsTrigger value="courses">Courses</TabsTrigger>
            <TabsTrigger value="ai-chat">AI Chat</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Google Analytics 4 Custom Charts */}
            <GA4Charts />

            {/* Conversion Funnel */}
            <Card>
              <CardHeader>
                <CardTitle>Conversion Funnel</CardTitle>
                <CardDescription>
                  User journey from visit to conversion
                </CardDescription>
              </CardHeader>
              <CardContent>
                {metricsLoading || toolInteractionsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-[#B87333]" />
                  </div>
                ) : (
                  <ConversionFunnel
                    totalSessions={metricsData?.uniqueSessions || 0}
                    pageViews={metricsData?.totalPageViews || 0}
                    toolInteractions={toolInteractionsCount || 0}
                    conversions={metricsData?.totalConversions || 0}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Conversions Tab */}
          <TabsContent value="conversions" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Conversion Sources</CardTitle>
                <CardDescription>
                  Where users are requesting consultations and booking calls
                </CardDescription>
              </CardHeader>
              <CardContent>
                {conversionsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-[#B87333]" />
                  </div>
                ) : conversionSources.length === 0 ? (
                  <div className="bg-slate-100 rounded-lg p-8 text-center border-2 border-dashed border-slate-300">
                    <MousePointerClick className="h-12 w-12 text-slate-400 mx-auto mb-3" />
                    <h3 className="text-lg font-bold text-slate-700 mb-2">
                      No Conversions Yet
                    </h3>
                    <p className="text-slate-600 text-sm max-w-md mx-auto">
                      Conversion data will appear here as users interact with
                      your tools and request consultations.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {conversionSources.map((source, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-white rounded-lg text-[#B87333]">
                            {source.icon}
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">
                              {source.source}
                            </p>
                            <p className="text-sm text-slate-600">
                              {source.percentage}% of total
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-[#B87333]">
                            {source.conversions}
                          </p>
                          <p className="text-xs text-slate-500">conversions</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tool Usage Tab */}
          <TabsContent value="tools" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Tool Performance</CardTitle>
                <CardDescription>
                  Usage statistics and engagement metrics for interactive tools
                </CardDescription>
              </CardHeader>
              <CardContent>
                {toolsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-[#B87333]" />
                  </div>
                ) : toolUsageStats.length === 0 ? (
                  <div className="bg-slate-100 rounded-lg p-8 text-center border-2 border-dashed border-slate-300">
                    <Calculator className="h-12 w-12 text-slate-400 mx-auto mb-3" />
                    <h3 className="text-lg font-bold text-slate-700 mb-2">
                      No Tool Usage Yet
                    </h3>
                    <p className="text-slate-600 text-sm max-w-md mx-auto">
                      Tool usage data will appear here as users interact with
                      the ROI Calculator, Feasibility Calculator, and Readiness
                      Assessment tools.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {toolUsageStats.map((tool, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-4 border-2 rounded-lg hover:border-[#B87333] transition-colors"
                      >
                        <div>
                          <p className="font-semibold text-slate-900 text-lg">
                            {tool.tool}
                          </p>
                          <p className="text-sm text-slate-600">
                            Avg. time: {tool.avgTime}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-3xl font-bold text-[#B87333]">
                            {tool.uses}
                          </p>
                          <p className="text-sm text-slate-600">total uses</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Trends Tab */}
          <TabsContent value="trends" className="space-y-6">
            <SessionMetricsChart
              data={sessionData || []}
              isLoading={sessionLoading}
            />
          </TabsContent>

          {/* Courses Tab */}
          <TabsContent value="courses" className="space-y-6">
            <CoursesPanel range={timeRange} />
          </TabsContent>

          {/* AI Chat Tab */}
          <TabsContent value="ai-chat" className="space-y-6">
            <ChatbarPanel range={timeRange} />
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-8">
            <UserBehaviorPanel range={timeRange} />
            <SubscriberAnalyticsPanel range={timeRange} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
